import React, { useState, useEffect } from 'react';
import { 
  MessageCircle, Settings, Phone, Key, CheckCircle2, 
  AlertCircle, Send, Loader2, Building
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../components/ui/card';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const WhatsAppSettings = () => {
  const [settings, setSettings] = useState({
    enabled: false,
    provider: 'meta',
    meta_phone_number_id: '',
    meta_access_token: '',
    meta_business_id: '',
    twilio_account_sid: '',
    twilio_auth_token: '',
    twilio_from_number: ''
  });
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isTesting, setIsTesting] = useState(false);
  const [testPhone, setTestPhone] = useState('');

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/api/whatsapp/settings`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-Hotel-Id': localStorage.getItem('selectedHotelId') || ''
        }
      });
      const data = await response.json();
      setSettings(data);
    } catch (error) {
      console.error('Failed to fetch WhatsApp settings:', error);
      toast.error('Failed to load WhatsApp settings');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/api/whatsapp/settings`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'X-Hotel-Id': localStorage.getItem('selectedHotelId') || ''
        },
        body: JSON.stringify(settings)
      });

      if (response.ok) {
        toast.success('WhatsApp settings saved successfully');
        fetchSettings(); // Refresh to get masked tokens
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to save settings');
      }
    } catch (error) {
      console.error('Failed to save WhatsApp settings:', error);
      toast.error('Failed to save settings');
    } finally {
      setIsSaving(false);
    }
  };

  const handleTestMessage = async () => {
    if (!testPhone) {
      toast.error('Please enter a phone number');
      return;
    }

    setIsTesting(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/api/whatsapp/test`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'X-Hotel-Id': localStorage.getItem('selectedHotelId') || ''
        },
        body: JSON.stringify({
          phone_number: testPhone,
          message: 'This is a test message from BIZIONIX HMS WhatsApp integration. If you received this, your setup is working correctly! 🎉'
        })
      });

      if (response.ok) {
        toast.success(`Test message sent to ${testPhone}`);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to send test message');
      }
    } catch (error) {
      console.error('Failed to send test message:', error);
      toast.error('Failed to send test message');
    } finally {
      setIsTesting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="p-6 max-w-4xl mx-auto" data-testid="whatsapp-settings-page">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
          <MessageCircle className="w-6 h-6 text-green-600" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">WhatsApp Settings</h1>
          <p className="text-gray-500">Configure WhatsApp for sending invoices and notifications</p>
        </div>
      </div>

      {/* Enable/Disable Toggle */}
      <Card className="mb-6">
        <CardContent className="pt-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                settings.enabled ? 'bg-green-100' : 'bg-gray-100'
              }`}>
                {settings.enabled ? (
                  <CheckCircle2 className="w-5 h-5 text-green-600" />
                ) : (
                  <AlertCircle className="w-5 h-5 text-gray-400" />
                )}
              </div>
              <div>
                <p className="font-medium text-gray-900">WhatsApp Integration</p>
                <p className="text-sm text-gray-500">
                  {settings.enabled ? 'Enabled - Invoices can be sent via WhatsApp' : 'Disabled'}
                </p>
              </div>
            </div>
            <Switch
              checked={settings.enabled}
              onCheckedChange={(checked) => setSettings({ ...settings, enabled: checked })}
            />
          </div>
        </CardContent>
      </Card>

      {/* Provider Selection */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="text-lg">WhatsApp Provider</CardTitle>
          <CardDescription>
            Choose your WhatsApp Business API provider
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Select
            value={settings.provider}
            onValueChange={(value) => setSettings({ ...settings, provider: value })}
          >
            <SelectTrigger className="w-full" data-testid="provider-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="meta">
                <div className="flex items-center gap-2">
                  <div className="w-6 h-6 bg-blue-100 rounded flex items-center justify-center">
                    <span className="text-xs font-bold text-blue-600">M</span>
                  </div>
                  Meta WhatsApp Cloud API
                </div>
              </SelectItem>
              <SelectItem value="twilio">
                <div className="flex items-center gap-2">
                  <div className="w-6 h-6 bg-red-100 rounded flex items-center justify-center">
                    <span className="text-xs font-bold text-red-600">T</span>
                  </div>
                  Twilio WhatsApp API
                </div>
              </SelectItem>
            </SelectContent>
          </Select>
        </CardContent>
      </Card>

      {/* Provider Configuration */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="text-lg">
            {settings.provider === 'meta' ? 'Meta WhatsApp Cloud API' : 'Twilio WhatsApp'} Configuration
          </CardTitle>
          <CardDescription>
            {settings.provider === 'meta' 
              ? 'Get credentials from Meta Business Suite → WhatsApp → API Setup'
              : 'Get credentials from Twilio Console → Account Info'
            }
          </CardDescription>
        </CardHeader>
        <CardContent>
          {settings.provider === 'meta' ? (
            <div className="space-y-4">
              <div>
                <Label>Phone Number ID</Label>
                <Input
                  value={settings.meta_phone_number_id}
                  onChange={(e) => setSettings({ ...settings, meta_phone_number_id: e.target.value })}
                  placeholder="Enter your WhatsApp Phone Number ID"
                  data-testid="meta-phone-id"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Found in Meta Business Suite → WhatsApp → API Setup
                </p>
              </div>
              <div>
                <Label>Access Token</Label>
                <Input
                  type="password"
                  value={settings.meta_access_token}
                  onChange={(e) => setSettings({ ...settings, meta_access_token: e.target.value })}
                  placeholder="Enter your permanent access token"
                  data-testid="meta-access-token"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Generate a permanent token in Meta Business Suite
                </p>
              </div>
              <div>
                <Label>Business ID (Optional)</Label>
                <Input
                  value={settings.meta_business_id}
                  onChange={(e) => setSettings({ ...settings, meta_business_id: e.target.value })}
                  placeholder="Enter your Meta Business ID"
                />
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div>
                <Label>Account SID</Label>
                <Input
                  value={settings.twilio_account_sid}
                  onChange={(e) => setSettings({ ...settings, twilio_account_sid: e.target.value })}
                  placeholder="ACxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                  data-testid="twilio-account-sid"
                />
              </div>
              <div>
                <Label>Auth Token</Label>
                <Input
                  type="password"
                  value={settings.twilio_auth_token}
                  onChange={(e) => setSettings({ ...settings, twilio_auth_token: e.target.value })}
                  placeholder="Enter your Twilio Auth Token"
                  data-testid="twilio-auth-token"
                />
              </div>
              <div>
                <Label>WhatsApp From Number</Label>
                <Input
                  value={settings.twilio_from_number}
                  onChange={(e) => setSettings({ ...settings, twilio_from_number: e.target.value })}
                  placeholder="whatsapp:+14155238886"
                  data-testid="twilio-from-number"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Format: whatsapp:+[country code][number]
                </p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Test Message */}
      {settings.enabled && (
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="text-lg">Send Test Message</CardTitle>
            <CardDescription>
              Verify your configuration by sending a test message
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex gap-3">
              <div className="flex-1">
                <Input
                  value={testPhone}
                  onChange={(e) => setTestPhone(e.target.value)}
                  placeholder="Enter phone number (e.g., 9876543210)"
                  data-testid="test-phone-input"
                />
              </div>
              <Button onClick={handleTestMessage} disabled={isTesting} data-testid="send-test-btn">
                {isTesting ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Send className="w-4 h-4 mr-2" />
                )}
                Send Test
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Save Button */}
      <div className="flex justify-end">
        <Button onClick={handleSave} disabled={isSaving} size="lg" data-testid="save-settings-btn">
          {isSaving ? (
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          ) : (
            <Settings className="w-4 h-4 mr-2" />
          )}
          Save Settings
        </Button>
      </div>

      {/* Help Section */}
      <Card className="mt-6 bg-blue-50 border-blue-200">
        <CardContent className="pt-6">
          <h3 className="font-medium text-blue-900 mb-2">Setup Instructions</h3>
          {settings.provider === 'meta' ? (
            <ol className="text-sm text-blue-800 space-y-1 list-decimal list-inside">
              <li>Go to <a href="https://business.facebook.com" target="_blank" rel="noopener noreferrer" className="underline">Meta Business Suite</a></li>
              <li>Navigate to WhatsApp → Getting Started</li>
              <li>Create or select a WhatsApp Business Account</li>
              <li>Get your Phone Number ID from the API Setup page</li>
              <li>Generate a permanent access token (System Users recommended)</li>
            </ol>
          ) : (
            <ol className="text-sm text-blue-800 space-y-1 list-decimal list-inside">
              <li>Go to <a href="https://console.twilio.com" target="_blank" rel="noopener noreferrer" className="underline">Twilio Console</a></li>
              <li>Find your Account SID and Auth Token on the dashboard</li>
              <li>Enable WhatsApp in Messaging → Try it Out → WhatsApp</li>
              <li>Get your WhatsApp-enabled Twilio number</li>
              <li>For production, apply for WhatsApp Business API access</li>
            </ol>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default WhatsAppSettings;
